/**
 * Currency Service
 * Handles currency conversion and formatting based on user preferences
 */

const PREFERENCES_KEY = 'mytherra_preferences';

// Exchange rates (you can update these or fetch from API)
const EXCHANGE_RATES = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  BTC: 0.000017 // Approximate, should be fetched from API
};

const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  BTC: '₿'
};

const currencyService = {
  /**
   * Get current currency preference
   * @returns {string} Currency code (USD, EUR, GBP, BTC)
   */
  getCurrentCurrency: () => {
    try {
      const prefs = localStorage.getItem(PREFERENCES_KEY);
      if (prefs) {
        const parsed = JSON.parse(prefs);
        return parsed.currency || 'USD';
      }
    } catch (e) {
      console.error('Error loading currency preference:', e);
    }
    return 'USD';
  },

  /**
   * Get currency symbol
   * @param {string} currency - Currency code
   * @returns {string} Currency symbol
   */
  getCurrencySymbol: (currency = null) => {
    const curr = currency || currencyService.getCurrentCurrency();
    return CURRENCY_SYMBOLS[curr] || '$';
  },

  /**
   * Convert USD to selected currency
   * @param {number} usdAmount - Amount in USD
   * @param {string} toCurrency - Target currency (optional, uses preference if not provided)
   * @returns {number} Converted amount
   */
  convertFromUSD: (usdAmount, toCurrency = null) => {
    if (!usdAmount || isNaN(usdAmount)) return 0;
    
    const targetCurrency = toCurrency || currencyService.getCurrentCurrency();
    const rate = EXCHANGE_RATES[targetCurrency] || 1;
    
    return usdAmount * rate;
  },

  /**
   * Format amount with currency symbol
   * @param {number} usdAmount - Amount in USD
   * @param {string} currency - Currency code (optional)
   * @param {number} decimals - Number of decimal places
   * @returns {string} Formatted amount with symbol
   */
  formatAmount: (usdAmount, currency = null, decimals = 2) => {
    const targetCurrency = currency || currencyService.getCurrentCurrency();
    const converted = currencyService.convertFromUSD(usdAmount, targetCurrency);
    const symbol = currencyService.getCurrencySymbol(targetCurrency);
    
    // For BTC, use more decimals
    const dec = targetCurrency === 'BTC' ? 8 : decimals;
    
    const formatted = converted.toLocaleString('en-US', {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec
    });
    
    return `${symbol}${formatted}`;
  },

  /**
   * Update exchange rates (call this periodically)
   * @param {Object} rates - New exchange rates
   */
  updateRates: (rates) => {
    Object.assign(EXCHANGE_RATES, rates);
  }
};

export default currencyService;
